/**
 * Device fingerprinting utility for guest subscription recognition
 * Generates a unique identifier for the current browser/device
 */

interface FingerprintData {
  userAgent: string;
  language: string;
  screen: string;
  timezone: number;
  canvas: string;
  webgl: string;
}

/**
 * Generates a canvas fingerprint
 */
const getCanvasFingerprint = (): string => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return 'no-canvas';

    canvas.width = 200;
    canvas.height = 50;

    // Add text with different fonts and styles
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('Device fingerprint 🔒', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('Device fingerprint 🔒', 4, 17);

    return canvas.toDataURL();
  } catch (error) {
    return 'canvas-error';
  }
};

/**
 * Generates a WebGL fingerprint
 */
const getWebGLFingerprint = (): string => {
  try {
    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')) as any;
    if (!gl) return 'no-webgl';

    const renderer = gl.getParameter(gl.RENDERER);
    const vendor = gl.getParameter(gl.VENDOR);
    
    return `${vendor}|${renderer}`;
  } catch (error) {
    return 'webgl-error';
  }
};

/**
 * Collects fingerprint data from the browser
 */
const collectFingerprintData = (): FingerprintData => {
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    screen: `${screen.width}x${screen.height}x${screen.colorDepth}`,
    timezone: new Date().getTimezoneOffset(),
    canvas: getCanvasFingerprint(),
    webgl: getWebGLFingerprint()
  };
};

/**
 * Generates a hash from the fingerprint data
 */
const hashFingerprint = (data: FingerprintData): string => {
  const combined = Object.values(data).join('|');
  
  // Simple hash function (you might want to use a crypto library in production)
  let hash = 0;
  for (let i = 0; i < combined.length; i++) {
    const char = combined.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return Math.abs(hash).toString(36).substring(0, 16);
};

/**
 * Generates a unique device fingerprint
 * @returns A unique string identifying this browser/device
 */
export const generateDeviceFingerprint = (): string => {
  try {
    const fingerprintData = collectFingerprintData();
    const hash = hashFingerprint(fingerprintData);
    
    // Add a timestamp component for additional uniqueness
    const timestamp = Date.now().toString(36).slice(-4);
    
    return `${hash}_${timestamp}`;
  } catch (error) {
    console.error('Error generating device fingerprint:', error);
    // Fallback to a random identifier
    return `fallback_${Math.random().toString(36).substring(2, 15)}`;
  }
};

/**
 * Validates if a stored fingerprint matches the current device
 * @param storedFingerprint The fingerprint to validate against
 * @returns True if the fingerprints match (excluding timestamp)
 */
export const validateDeviceFingerprint = (storedFingerprint: string): boolean => {
  try {
    const currentFingerprint = generateDeviceFingerprint();
    
    // Extract the hash part (before the timestamp)
    const storedHash = storedFingerprint.split('_')[0];
    const currentHash = currentFingerprint.split('_')[0];
    
    return storedHash === currentHash;
  } catch (error) {
    console.error('Error validating device fingerprint:', error);
    return false;
  }
};

/**
 * Gets a stable device identifier (without timestamp)
 * This is useful for comparing devices across sessions
 */
export const getStableDeviceId = (): string => {
  try {
    const fingerprintData = collectFingerprintData();
    return hashFingerprint(fingerprintData);
  } catch (error) {
    console.error('Error generating stable device ID:', error);
    return `stable_${Math.random().toString(36).substring(2, 10)}`;
  }
};

